import React from 'react';
import PropTypes from 'prop-types';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import Label from '../../../CommonComponents/Label';
import { Alert } from '../../../CommonComponents/Alert/Alert';
import Input from '../../../CommonComponents/Input';
import Form from '../../../CommonComponents/Form';
import StyledMaskedInput from '../../../CommonComponents/MaskedInput';
import {
  ActionButton,
  Actions,
  DeleteButton,
  DropDownTrigger,
  InlineDropdownContainer,
  Optional,
  Wrapper,
  Title,
  StyledModal,
  StyledDropdown,
  SearchableDropdown,
} from './StyledComponents';

const View = ({
  accountsList,
  clientId,
  closeModal,
  daysOptions,
  draftTransaction,
  categoriesList,
  isOpen,
  monthsOptions,
  saveTransaction,
  updateDraftTransaction,
  yearOptions,
  errors,
  isEdit,
  clearErrors,
  openDeleteModal,
  formErrors,
  expenseCategoryFilter,
  currentAccount,
  currentPeriodId,
  currentQuery,
  pagination: { currentPage },
}) => {
  const transactionYear = yearOptions.find(year => year.value === draftTransaction.year);

  return (
    <StyledModal isOpen={isOpen} showCloseModalCross onRequestClose={closeModal}>
      <Form onSubmit={() => {}}>
        <Wrapper>
          <Title>{`${isEdit ? 'Edit' : 'Add'} Transaction`}</Title>
          {errors.length > 0 && (
            <Alert appearance="info" message={errors} onRequestClose={clearErrors} />
          )}
          <Form.Control>
            <Label>Transaction Date</Label>
            <InlineDropdownContainer>
              <SearchableDropdown
                options={monthsOptions}
                value={{
                  label: monthsOptions[draftTransaction.month - 1]
                    ? monthsOptions[draftTransaction.month - 1].label
                    : '',
                  value: draftTransaction.month,
                }}
                placeholder="Month"
                onChange={option => {
                  updateDraftTransaction({
                    ...draftTransaction,
                    month: option.value,
                  });
                }}
              >
                <DropDownTrigger appendIcon="fa fa-caret-down" />
              </SearchableDropdown>
              <SearchableDropdown
                options={daysOptions}
                value={{
                  value: draftTransaction.day,
                  label: daysOptions[draftTransaction.day - 1]
                    ? daysOptions[draftTransaction.day - 1].label
                    : '',
                }}
                placeholder="Day"
                onChange={option => {
                  updateDraftTransaction({
                    ...draftTransaction,
                    day: option.value,
                  });
                }}
              >
                <DropDownTrigger appendIcon="fa fa-caret-down" />
              </SearchableDropdown>

              <SearchableDropdown
                options={yearOptions}
                value={{
                  value: draftTransaction.year,
                  label:
                    yearOptions && draftTransaction.year && transactionYear
                      ? transactionYear.label
                      : isEdit
                      ? draftTransaction.year
                      : '',
                }}
                placeholder="Year"
                onChange={option => {
                  updateDraftTransaction({
                    ...draftTransaction,
                    year: option.value,
                  });
                }}
              >
                <DropDownTrigger appendIcon="fa fa-caret-down" />
              </SearchableDropdown>
            </InlineDropdownContainer>
          </Form.Control>

          <Form.Control>
            <Label id="description" forHtml="description">
              Description
            </Label>
            <Input.Big
              name="description"
              value={draftTransaction.description}
              onChange={({ target: { value } }) => {
                updateDraftTransaction({
                  ...draftTransaction,
                  description: value,
                });
              }}
            />
          </Form.Control>
          {draftTransaction.isManuallyCreated && (
            <Form.Control>
              <Label id="amount" forHtml="amount">
                Amount
              </Label>
              <StyledMaskedInput
                mask={createNumberMask({
                  prefix: '',
                  allowDecimal: true,
                  allowNegative: true,
                })}
                showIcon
                name="amount"
                value={draftTransaction.amount}
                onChange={({ target: { value } }) => {
                  updateDraftTransaction({
                    ...draftTransaction,
                    amount: value,
                  });
                }}
                prependIcon="fa fa-dollar-sign"
              />
            </Form.Control>
          )}

          <Form.Control>
            <Label id="category" forHtml="category">
              Add to a Transaction Category
            </Label>

            <StyledDropdown
              IEProps={{
                showLessOptions: true,
              }}
              isSearchable
              items={categoriesList}
              value={draftTransaction.category}
              onChange={option => {
                updateDraftTransaction({
                  ...draftTransaction,
                  category: option.value,
                });
              }}
            >
              <DropDownTrigger appendIcon="fa fa-caret-down">
                {draftTransaction.category}
              </DropDownTrigger>
            </StyledDropdown>
          </Form.Control>

          <Form.Control>
            <Label id="amount" forHtml="amount">
              Account Name <Optional>(Optional)</Optional>
            </Label>

            <SearchableDropdown
              options={accountsList}
              openDirectionUp
              value={draftTransaction.account}
              onChange={option => {
                updateDraftTransaction({
                  ...draftTransaction,
                  account: option,
                });
              }}
            >
              <DropDownTrigger appendIcon="fa fa-caret-down">
                {draftTransaction.account.label}
              </DropDownTrigger>
            </SearchableDropdown>
          </Form.Control>
        </Wrapper>

        <Actions>
          {isEdit && (
            <DeleteButton
              appearance="secondary"
              prependIcon="far fa-trash-alt"
              onClick={() => {
                openDeleteModal();
              }}
            >
              Delete
            </DeleteButton>
          )}
          <ActionButton
            appearance="primary"
            onClick={() => {
              clearErrors();
              saveTransaction(
                formErrors,
                draftTransaction,
                clientId,
                currentAccount.accountId,
                currentPeriodId,
                currentQuery,
                expenseCategoryFilter,
                currentPage,
              );
            }}
          >
            {isEdit ? 'Save' : 'Add'}
          </ActionButton>
        </Actions>
      </Form>
    </StyledModal>
  );
};

View.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  accountsList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ).isRequired,
  categoriesList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ).isRequired,
  draftTransaction: PropTypes.shape({
    month: PropTypes.string,
    year: PropTypes.string,
    day: PropTypes.string,
    description: PropTypes.string,
    category: PropTypes.shape({}),
    account: PropTypes.shape({}),
  }).isRequired,
  updateDraftTransaction: PropTypes.func.isRequired,
  saveTransaction: PropTypes.func.isRequired,
  clientId: PropTypes.string.isRequired,
  monthsOptions: PropTypes.shape({}).isRequired,
  daysOptions: PropTypes.shape({}).isRequired,
  yearOptions: PropTypes.shape({}).isRequired,
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
  isEdit: PropTypes.bool.isRequired,
  clearErrors: PropTypes.func.isRequired,
  openDeleteModal: PropTypes.func.isRequired,
  formErrors: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default View;
